import {
  BankMutation,
  CreateBankTransactionModel,
  CreateJournalEntryDto,
  CreateJournalEntryLineDto,
  Currency,
  JournalEntryModel,
  JournalEntryLineModel,
  JournalEntrySourceType,
  JOURNAL_ENTRY_SOURCE_TYPES,
  TransactionType,
  ContributionPlanWithPayments,
  ContributionFrequency,
} from '../types/bankAccount';
import { Connection } from '../redux/slices/connectionsSlice';

export function buildCombinedTransactionModels(
  confirmedMatches: { transaction: CreateBankTransactionModel; matchedConnection: Connection; contributionPlanId?: number }[],
  selectedGroupMutations: CreateBankTransactionModel[],
): CreateBankTransactionModel[] {
  // create the confirmed matches as CreateBankTransactionModel
  const matchedModels: CreateBankTransactionModel[] = confirmedMatches.map(
    ({ transaction, matchedConnection, contributionPlanId }) => ({
      ...transaction,
      transactionType: contributionPlanId
        ? TransactionType.CONTRIBUTION
        : (transaction.amount < 0 ? TransactionType.PERSONAL_EXPENSE : TransactionType.PERSONAL_INCOME),
      participations: contributionPlanId ? undefined : [
        {
          dashboardMemberId: +matchedConnection.dashboardMemberId,
          amount: transaction.amount,
        },
      ],
      contributionPayment: contributionPlanId ? {
        contributionPlanId,
        dashboardMemberId: +matchedConnection.dashboardMemberId,
      } : undefined,
    }),
  );

  // create the selectedGroupMutataions as group transactions
  const unmatchedSplitModels: CreateBankTransactionModel[] = selectedGroupMutations.map(
    (transaction) => {
      return {
        ...transaction,
        participations: undefined, // no participations for group transactions
        contributionPayment: undefined,
        transactionType:
          transaction.amount < 0 ? TransactionType.GROUP_EXPENSE : TransactionType.GROUP_INCOME,
      };
    },
  );

  return [...matchedModels, ...unmatchedSplitModels];
}

export function matchMutationsToConnections(
  mutations: BankMutation[],
  connections: Connection[],
): Array<{
  transaction: CreateBankTransactionModel;
  matchedConnection?: Connection;
}> {
  const connectionNames = connections.map((c) => ({
    firstName: c.fields.FIRST_NAME,
    lastName: c.fields.LAST_NAME,
    connection: c,
  }));

  return mutations.map((t) => {
    const matchesBasedOnLastName = connectionNames.filter((c) => {
      const lastNameNormalized = c.lastName.toLowerCase().trim();
      return t.oppositeName.toLowerCase().trim().includes(lastNameNormalized);
    });

    const transactionModel = buildTransactionFromMutation(t);

    if (matchesBasedOnLastName.length === 0)
      return { transaction: transactionModel, matchedConnection: undefined };
    if (matchesBasedOnLastName.length === 1)
      return {
        transaction: transactionModel,
        matchedConnection: matchesBasedOnLastName[0].connection,
      };

    const matchesBasedOnFirstName = matchesBasedOnLastName.filter((c) => {
      const firstNameNormalized = c.firstName.toLowerCase().trim();
      return (
        t.oppositeName.toLowerCase().trim().includes(firstNameNormalized) ||
        t.oppositeName
          .toLowerCase()
          .trim()
          .split(/\s+/)
          .some((token) => token.at(0) === firstNameNormalized.at(0))
      );
    });

    if (matchesBasedOnFirstName.length === 0)
      return {
        transaction: transactionModel,
        matchedConnection: matchesBasedOnLastName[0].connection,
      };

    return {
      transaction: transactionModel,
      matchedConnection: matchesBasedOnFirstName[0].connection,
    };
  });
}

// export async function updateTransactionWithSplits(
//   tns: BankTransaction | null,
//   participations: BankTransactionParticipation[],
// ): Promise<BankTransaction> {
//   if (!tns) return Promise.reject(new Error('Transaction is null or undefined'));

//   const newTns = {
//     ...tns,
//     participations: participations.map((p) => ({
//       ...p,
//       amount: Math.floor(p.amount),
//     })),
//   };

//   await bankAccountService.updateBankTransaction(tns.transactionId, newTns);

//   return newTns;
// }

/**
 * @deprecated Use buildJournalEntryFromMutation instead
 */
export function buildTransactionFromMutation(mutation: BankMutation): CreateBankTransactionModel {
  const transactionType =
    mutation.amount >= 0 ? TransactionType.GROUP_INCOME : TransactionType.GROUP_EXPENSE;

  return {
    amount: mutation.amount,
    description: `${mutation.oppositeName}, ${mutation.description}`,
    date: mutation.transactionDate,
    mutationId: mutation.mutationId,
    transactionType,
  };
}

/**
 * Build a journal entry DTO from a bank mutation
 * Note: Requires ledger account IDs to be provided
 */
export function buildJournalEntryFromMutation(
  mutation: BankMutation,
  expenseLedgerAccountId?: number,
  incomeLedgerAccountId?: number,
  walletLedgerAccountId?: number,
  currency: Currency = Currency.EUR,
): CreateJournalEntryDto {
  const sourceType =
    mutation.amount >= 0
      ? JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME
      : JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE;

  // If mutationId is present, backend will auto-generate lines for GROUP_EXPENSE/GROUP_INCOME
  // Only build lines manually if mutationId is not available
  const lines: CreateJournalEntryLineDto[] = [];
  
  // For GROUP_EXPENSE and GROUP_INCOME with mutationId, backend handles lines automatically
  // Only build lines if we don't have a mutationId
  if (mutation.mutationId === undefined) {
    const absAmount = Math.abs(mutation.amount);

    if (mutation.amount < 0) {
      // Group expense
      if (expenseLedgerAccountId) {
        lines.push({
          ledgerAccountId: expenseLedgerAccountId,
          debit: absAmount,
          credit: 0,
          currency,
        });
      }
      if (walletLedgerAccountId) {
        lines.push({
          ledgerAccountId: walletLedgerAccountId,
          debit: 0,
          credit: absAmount,
          currency,
        });
      }
    } else {
      // Group income
      if (incomeLedgerAccountId) {
        lines.push({
          ledgerAccountId: incomeLedgerAccountId,
          debit: 0,
          credit: absAmount,
          currency,
        });
      }
      if (walletLedgerAccountId) {
        lines.push({
          ledgerAccountId: walletLedgerAccountId,
          debit: absAmount,
          credit: 0,
          currency,
        });
      }
    }
  }

  return {
    dashboardId: 0, // Should be set by caller
    entryDate: mutation.transactionDate,
    description: `${mutation.oppositeName}, ${mutation.description}`,
    sourceType,
    mutationId: mutation.mutationId,
    lines,
  };
}

export function formatMoney(amount: number, locale: string, currency: string): string {
  if (amount === null || amount === undefined) return '';

  const options = {
    style: 'currency' as const,
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return new Intl.NumberFormat(locale, options).format(Math.abs(amount));
}

export function formatMoneyWithSign(
  amount: number,
  locale: string,
  currency: string,
  showPlus: boolean = true,
): string {
  if (amount === null || amount === undefined) return '';

  const baseFormatted = formatMoney(Math.abs(amount), locale, currency);

  if (amount > 0) return showPlus ? `+${baseFormatted}` : baseFormatted;
  if (amount < 0) return `−${baseFormatted}`;
  return baseFormatted; // No sign for 0
}

export function formatEuroCents(amount: number, locale: string): string {
  return formatMoney(amount / 100, locale, 'EUR');
}

export function formatEuroCentsWithSign(
  amount: number,
  locale: string,
  showPlus: boolean = true,
): string {
  return formatMoneyWithSign(amount / 100, locale, 'EUR', showPlus);
}

// Currency utility functions for ledger-based system
export function formatBalance(cents: number, currency: Currency, locale: string): string {
  return formatMoney(cents / 100, locale, currency);
}

export function formatBalanceWithSign(
  cents: number,
  currency: Currency,
  locale: string,
  showPlus: boolean = true,
): string {
  return formatMoneyWithSign(cents / 100, locale, currency, showPlus);
}

export function centsToCurrency(cents: number): number {
  return cents / 100;
}

export function currencyToCents(amount: number): number {
  return Math.round(amount * 100);
}

// ============================================================================
// Journal Entry Utility Functions
// ============================================================================

/**
 * Calculate net amount from journal entry lines (sum of debits - sum of credits)
 * Note: In double-entry bookkeeping, this will always be 0
 */
export function calculateNetAmount(entry: JournalEntryModel): number {
  if (!entry.lines || entry.lines.length === 0) return 0;
  return entry.lines.reduce((sum, line) => sum + line.debit - line.credit, 0);
}

/**
 * Calculate display amount for a journal entry based on source type
 * This shows the actual transaction amount (positive for income, negative for expenses)
 * instead of the net amount which is always 0 in double-entry bookkeeping
 */
export function calculateDisplayAmount(entry: JournalEntryModel): number {
  // Prefer explicit amount field when present (for newer entries)
  const anyEntry = entry as any;
  if (typeof anyEntry.amount === 'number' && !Number.isNaN(anyEntry.amount)) {
    return anyEntry.amount;
  }

  if (!entry.lines || entry.lines.length === 0) return 0;

  const { sourceType } = entry;
  
  // Calculate totals once
  const totalDebits = entry.lines.reduce((sum, line) => sum + line.debit, 0);
  const totalCredits = entry.lines.reduce((sum, line) => sum + line.credit, 0);
  
  // For INITIAL_BALANCE: respect the sign of the amount
  // In double-entry bookkeeping, totalDebits = totalCredits, so we need to identify
  // the wallet account line. The wallet account line typically has participantMemberId set.
  // For initial balance (API convention):
  // - Positive balance: wallet credited (credit on wallet line) → return positive
  // - Negative balance: wallet debited (debit on wallet line) → return negative
  if (sourceType === JOURNAL_ENTRY_SOURCE_TYPES.INITIAL_BALANCE) {
    // Find the wallet account line using the entry's dashboardMemberId
    // The wallet line should have participantMemberId matching entry.dashboardMemberId
    const memberId = entry.dashboardMemberId;
    if (memberId !== undefined && memberId !== null) {
      const walletLine = entry.lines.find(line => 
        line.participantMemberId === memberId && 
        (line.debit > 0 || line.credit > 0)
      );
      
      if (walletLine) {
        // Wallet account line found - use its debit/credit to determine sign
        // Flipped: credit = positive balance, debit = negative balance
        if (walletLine.credit > 0) {
          return walletLine.credit; // Positive balance (wallet credited)
        }
        if (walletLine.debit > 0) {
          return -walletLine.debit; // Negative balance (wallet debited)
        }
      }
    }
    
    // Fallback: try to find any line with participantMemberId set
    const memberLine = entry.lines.find(line => 
      line.participantMemberId !== null && 
      (line.debit > 0 || line.credit > 0)
    );
    
    if (memberLine) {
      if (memberLine.credit > 0) {
        return memberLine.credit;
      }
      if (memberLine.debit > 0) {
        return -memberLine.debit;
      }
    }
    
    return 0;
  }
  
  // Income types: show positive amount
  if (
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME ||
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN
  ) {
    if (sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME) {
      // For personal income, derive the amount from the payer's member lines
      // to avoid double-counting any additional balancing lines.
      const memberId = entry.payerId ?? entry.dashboardMemberId ?? null;
      if (memberId !== null) {
        const memberLines = entry.lines.filter(
          (line) => line.participantMemberId === memberId,
        );
        if (memberLines.length > 0) {
          const memberDebits = memberLines.reduce((sum, l) => sum + l.debit, 0);
          const memberCredits = memberLines.reduce((sum, l) => sum + l.credit, 0);
          const absAmount = Math.max(memberDebits, memberCredits);
          if (absAmount > 0) {
            return absAmount;
          }
        }
      }
      // Fallback: use overall totals if we couldn't isolate member lines
      return totalDebits > 0 ? totalDebits : totalCredits;
    }

    // Group income / contribution run: normal behaviour
    return totalDebits > 0 ? totalDebits : totalCredits;
  }
  
  // Expense types: show negative amount
  if (
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE ||
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE
  ) {
    // For expense: sum of debits (expense account) but make negative
    // or sum of credits (wallet) as negative
    // Return negative of the larger amount (usually debits for expense)
    return totalDebits > 0 ? -totalDebits : -totalCredits;
  }
  
  // For INTERNAL_TRANSFER, SAVING_POT_FUND, SAVING_POT_RELEASE, BANK_RECONCILIATION
  // Show the transfer amount (sum of debits or credits, whichever is larger)
  
  // For internal transfers, we might want to show 0 or the transfer amount
  // For now, show the amount (positive)
  if (sourceType === JOURNAL_ENTRY_SOURCE_TYPES.INTERNAL_TRANSFER) {
    return totalDebits > 0 ? totalDebits : totalCredits;
  }
  
  // For other types, default to showing debits (or credits if larger)
  return totalDebits > totalCredits ? totalDebits : totalCredits;
}

/**
 * Calculate the next payment date for a contribution plan based on member's payment history
 * Returns null if the plan is complete (all installments paid)
 */
export function calculateNextPaymentDate(
  plan: ContributionPlanWithPayments,
  dashboardMemberId: number,
): Date | null {
  // Get member's payments sorted by date (most recent first)
  const memberPayments = plan.contributionPayments.filter(
    (p) => p.dashboardMemberId === dashboardMemberId,
  );
  
  const sortedPayments = memberPayments
    .map((p) => (p.journalEntry?.entryDate ? new Date(p.journalEntry.entryDate) : null))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime());
  
  const paymentCount = sortedPayments.length;
  const lastPaymentDate = sortedPayments[0];
  
  // If plan is complete (all installments paid)
  if (paymentCount >= plan.numberOfInstallments) {
    return null;
  }
  
  // Calculate next payment date
  let nextDate: Date;
  if (lastPaymentDate) {
    nextDate = new Date(lastPaymentDate);
  } else {
    nextDate = new Date(plan.startDate);
  }
  
  // Add frequency interval
  switch (plan.frequency) {
    case ContributionFrequency.MONTHLY:
      nextDate.setMonth(nextDate.getMonth() + 1);
      break;
    case ContributionFrequency.QUARTERLY:
      nextDate.setMonth(nextDate.getMonth() + 3);
      break;
    case ContributionFrequency.YEARLY:
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      break;
    case ContributionFrequency.ONCE:
      return paymentCount > 0 ? null : nextDate;
    default:
      return null;
  }
  
  return nextDate;
}

/**
 * Get journal entry lines for a specific member (filtered by participantMemberId)
 */
export function getJournalEntryLinesForMember(
  entry: JournalEntryModel,
  memberId: number,
): JournalEntryLineModel[] {
  if (!entry.lines) return [];
  return entry.lines.filter((line) => line.participantMemberId === memberId);
}

/**
 * Calculate the wallet impact for a member from a journal entry
 * This calculates how the entry affects the member's wallet balance
 * For GROUP_EXPENSE where member is payer: positive (they're owed money)
 * For income: positive (money coming in)
 * For expenses where member is participant: negative (their share of expense)
 */
export function calculateMemberWalletImpact(
  entry: JournalEntryModel,
  memberId: number,
): number {
  if (!entry.lines || entry.lines.length === 0) return 0;
  
  // For INITIAL_BALANCE entries, find the wallet account line
  // The wallet account line should have participantMemberId matching the member
  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.INITIAL_BALANCE) {
    // Find the wallet account line for this member
    // The wallet line typically has participantMemberId set to the member
    const walletLine = entry.lines.find(line => 
      line.participantMemberId === memberId && 
      (line.debit > 0 || line.credit > 0)
    );
    
    if (walletLine) {
      // Wallet account line found - use its debit/credit to determine sign
      // Flipped: credit = positive balance, debit = negative balance
      if (walletLine.credit > 0) {
        return walletLine.credit; // Positive balance (wallet credited)
      }
      if (walletLine.debit > 0) {
        return -walletLine.debit; // Negative balance (wallet debited)
      }
    }
    
    // Fallback: if no wallet line found with participantMemberId, use calculateDisplayAmount
    // This handles cases where the wallet line doesn't have participantMemberId set
    const displayAmount = calculateDisplayAmount(entry);
    return displayAmount;
  }
  
  const memberLines = entry.lines.filter((line) => line.participantMemberId === memberId);
  
  // If no member lines, use display amount (for entries without participant lines)
  if (memberLines.length === 0) {
    const displayAmount = calculateDisplayAmount(entry);
    // For GROUP_EXPENSE where member is payer, flip sign
    if (
      entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE &&
      entry.payerId === memberId
    ) {
      return -displayAmount;
    }
    // For CONTRIBUTION_RUN: contributions are expenses (negative)
    if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN) {
      return -Math.abs(displayAmount);
    }
    // For SAVING_POT_FUND: money is put into saving pot (deducted from wallet - negative)
    if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_FUND) {
      return -Math.abs(displayAmount);
    }
    // For SAVING_POT_RELEASE: money is released from saving pot (added to wallet - positive)
    if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_RELEASE) {
      return Math.abs(displayAmount);
    }
    return displayAmount;
  }
  
  // For entries with member lines, calculate wallet impact
  // In double-entry bookkeeping, member lines typically include both:
  // - Expense/Income line (debit for expense, credit for income)
  // - Wallet line (credit for expense, debit for income)
  // These cancel out to 0, so we need to identify the wallet impact
  
  // Calculate total debits and credits for member lines
  const totalDebits = memberLines.reduce((sum, line) => sum + line.debit, 0);
  const totalCredits = memberLines.reduce((sum, line) => sum + line.credit, 0);
  
  // Get the absolute amount (debits and credits should be equal in double-entry)
  const absAmount = Math.max(totalDebits, totalCredits);
  
  // For GROUP_EXPENSE where member is payer:
  // - Wallet line has credit (money out of wallet)
  // - But from payer's perspective: they're owed money (positive)
  if (
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE &&
    entry.payerId === memberId
  ) {
    // Payer is owed money, so positive impact
    return absAmount;
  }
  
  // For income types: wallet is debited (positive impact)
  if (
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME ||
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME
  ) {
    return absAmount;
  }
  
  // For CONTRIBUTION_RUN: wallet is credited (negative impact - money deducted from wallet)
  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN) {
    return -absAmount;
  }
  
  // For SAVING_POT_FUND: wallet is credited (negative impact - money deducted from wallet into pot)
  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_FUND) {
    return -absAmount;
  }
  
  // For SAVING_POT_RELEASE: wallet is debited (positive impact - money released from pot to wallet)
  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_RELEASE) {
    return absAmount;
  }
  
  
  // For expense types where member is participant (not payer):
  // Wallet is credited (negative impact - their share of expense)
  if (
    entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE ||
    (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE && entry.payerId !== memberId)
  ) {
    return -absAmount;
  }
  
  // Default: use net (debit - credit), though this should typically be 0 for member lines
  return totalDebits - totalCredits;
}

/**
 * Convert TransactionType to JournalEntrySourceType
 */
export function convertTransactionTypeToSourceType(type: TransactionType): JournalEntrySourceType {
  switch (type) {
    case TransactionType.PERSONAL_EXPENSE:
      return JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE;
    case TransactionType.PERSONAL_INCOME:
      return JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME;
    case TransactionType.GROUP_EXPENSE:
      return JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE;
    case TransactionType.GROUP_INCOME:
      return JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME;
    case TransactionType.CONTRIBUTION:
      return JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN;
    case TransactionType.INTERNAL_BANK_TRANSFER:
      return JOURNAL_ENTRY_SOURCE_TYPES.INTERNAL_TRANSFER;
    default:
      return JOURNAL_ENTRY_SOURCE_TYPES.BANK_RECONCILIATION;
  }
}

/**
 * Build journal entry lines from amount and transaction type
 * This is a simplified helper - in practice, you need to specify ledger accounts
 */
export function buildJournalEntryLines(
  amount: number,
  sourceType: JournalEntrySourceType,
  currency: Currency = Currency.EUR,
  expenseLedgerAccountId?: number,
  incomeLedgerAccountId?: number,
  walletLedgerAccountId?: number,
): CreateJournalEntryLineDto[] {
  const absAmount = Math.abs(amount);
  const lines: CreateJournalEntryLineDto[] = [];

  // For expenses (negative amounts or expense types)
  if (amount < 0 || sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE || 
      sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE) {
    if (expenseLedgerAccountId) {
      lines.push({
        ledgerAccountId: expenseLedgerAccountId,
        debit: absAmount,
        credit: 0,
        currency,
      });
    }
    if (walletLedgerAccountId) {
      lines.push({
        ledgerAccountId: walletLedgerAccountId,
        debit: 0,
        credit: absAmount,
        currency,
      });
    }
  }
  // For income (positive amounts or income types)
  else if (amount > 0 || sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME || 
           sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
           sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN) {
    if (incomeLedgerAccountId) {
      lines.push({
        ledgerAccountId: incomeLedgerAccountId,
        debit: 0,
        credit: absAmount,
        currency,
      });
    }
    if (walletLedgerAccountId) {
      lines.push({
        ledgerAccountId: walletLedgerAccountId,
        debit: absAmount,
        credit: 0,
        currency,
      });
    }
  }

  return lines;
}

/**
 * Convert old BankTransaction model to CreateJournalEntryDto
 * Note: This requires ledger account IDs which may not be available in the old model
 */
export function transactionToJournalEntryDto(
  tx: CreateBankTransactionModel,
  expenseLedgerAccountId?: number,
  incomeLedgerAccountId?: number,
  walletLedgerAccountId?: number,
  currency: Currency = Currency.EUR,
): CreateJournalEntryDto {
  const sourceType = convertTransactionTypeToSourceType(tx.transactionType);
  
  // Build lines from amount and participations
  const lines: CreateJournalEntryLineDto[] = [];
  
  if (tx.participations && tx.participations.length > 0) {
    // Personal transaction with participations
    tx.participations.forEach((participation) => {
      const absAmount = Math.abs(participation.amount);
      if (participation.amount < 0) {
        // Expense
        if (expenseLedgerAccountId) {
          lines.push({
            ledgerAccountId: expenseLedgerAccountId,
            participantMemberId: participation.dashboardMemberId,
            debit: absAmount,
            credit: 0,
            currency,
          });
        }
        if (walletLedgerAccountId) {
          lines.push({
            ledgerAccountId: walletLedgerAccountId,
            participantMemberId: participation.dashboardMemberId,
            debit: 0,
            credit: absAmount,
            currency,
          });
        }
      } else {
        // Income
        if (incomeLedgerAccountId) {
          lines.push({
            ledgerAccountId: incomeLedgerAccountId,
            participantMemberId: participation.dashboardMemberId,
            debit: 0,
            credit: absAmount,
            currency,
          });
        }
        if (walletLedgerAccountId) {
          lines.push({
            ledgerAccountId: walletLedgerAccountId,
            participantMemberId: participation.dashboardMemberId,
            debit: absAmount,
            credit: 0,
            currency,
          });
        }
      }
    });
  } else {
    // Group transaction - use the main amount
    const absAmount = Math.abs(tx.amount);
    if (tx.amount < 0) {
      // Group expense
      if (expenseLedgerAccountId) {
        lines.push({
          ledgerAccountId: expenseLedgerAccountId,
          debit: absAmount,
          credit: 0,
          currency,
        });
      }
      if (walletLedgerAccountId) {
        lines.push({
          ledgerAccountId: walletLedgerAccountId,
          debit: 0,
          credit: absAmount,
          currency,
        });
      }
    } else {
      // Group income
      if (incomeLedgerAccountId) {
        lines.push({
          ledgerAccountId: incomeLedgerAccountId,
          debit: 0,
          credit: absAmount,
          currency,
        });
      }
      if (walletLedgerAccountId) {
        lines.push({
          ledgerAccountId: walletLedgerAccountId,
          debit: absAmount,
          credit: 0,
          currency,
        });
      }
    }
  }

  return {
    dashboardId: 0, // Should be set by caller
    dashboardMemberId: tx.payerId,
    payerId: tx.payerId,
    processedByDashboardMemberId: tx.processedByDashboardMemberId,
    entryDate: tx.date,
    description: tx.description,
    sourceType,
    sourceId: undefined,
    mutationId: tx.mutationId,
    amount: tx.amount, // Amount in cents
    currency, // Currency code
    lines,
  };
}
